import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RiMailLine,
  RiPhoneLine,
  RiLockLine,
  RiShieldCheckLine,
  RiBuildingLine,
  RiPaletteLine,
  RiFileTextLine,
  RiTeamLine,
  RiBankLine,
} from '@remixicon/react';
import { apiClient } from '@/lib/api';
import { Skeleton } from '@components/ui/Skeleton';
import { useAuthStore } from '@store/authStore';
import { useAppStore } from '@store/appStore';
import {
  SettingsSection,
  SettingsRow,
  RoleBadge,
  UpdatePhoneModal,
  ChangePasswordModal,
  EditOrganizationDrawer,
  UpdateBrandingDrawer,
  StandardContractModal,
  TwoFactorAuthModal,
  TeamMembersPanel,
  AddMemberDrawer,
} from '../components';
import { initials, formatPhone } from '../utils/format';
import { encodeData } from '@/utils';
import { buildTeamInviteEmailHtml } from '@/utils/emailTemplates';
import { toast } from 'sonner';

const SkeletonRow = () => (
  <div className="flex items-center gap-4 px-4 py-3.5">
    <Skeleton className="w-5 h-5 rounded" />
    <div className="flex-1 space-y-1.5">
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-3 w-1/3" />
    </div>
    <Skeleton className="w-4 h-4 rounded" />
  </div>
);

const AccountSettings = () => {
  const queryClient = useQueryClient();
  const { userProfile, user, fetchUserProfile } = useAuthStore();
  const { setPageHeaderOverride, clearPageHeaderOverride } = useAppStore();
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [orgDrawerOpen, setOrgDrawerOpen] = useState(false);
  const [brandingDrawerOpen, setBrandingDrawerOpen] = useState(false);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [mfaModalOpen, setMfaModalOpen] = useState(false);
  const [teamViewOpen, setTeamViewOpen] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  const firstName = userProfile?.first_name || '';
  const lastName = userProfile?.last_name || '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || 'User';
  const email = userProfile?.email || user?.email || '';
  const phone = formatPhone(userProfile?.phone);
  const role = userProfile?.role;
  const isAdmin = ['admin', 'ADMIN'].includes(role);
  const hasPasswordLogin = !!user?.identities?.some((i) => i.provider === 'email');
  const oauthProvider = user?.identities?.find((i) => i.provider !== 'email')?.provider;
  const providerLabel = oauthProvider
    ? oauthProvider.charAt(0).toUpperCase() + oauthProvider.slice(1)
    : '';

  const { data: organization, isLoading: orgLoading } = useQuery({
    queryKey: ['organization', userProfile?.org_id],
    queryFn: () => apiClient.getOrganizationById(userProfile.org_id),
    enabled: !!userProfile?.org_id,
  });

  useEffect(() => {
    if (teamViewOpen) {
      setPageHeaderOverride({
        title: <span className="text-2xl font-bold text-white leading-tight">Team Members</span>,
        subtitle: organization?.name || '',
        onBack: () => setTeamViewOpen(false),
      });
    } else {
      setPageHeaderOverride({
        title: (
          <span className="flex items-center gap-4">
            <span className="w-14 h-14 rounded-full bg-amber-100 text-amber-900 flex items-center justify-center text-lg font-bold flex-shrink-0">
              {initials(firstName, lastName)}
            </span>
            <span className="flex flex-col">
              <span className="text-2xl font-bold text-white leading-tight">{fullName}</span>
              <span className="flex flex-wrap items-center gap-2 mt-1">
                {role && <RoleBadge>{role}</RoleBadge>}
                {isAdmin && <RoleBadge>Admin</RoleBadge>}
              </span>
            </span>
          </span>
        ),
      });
    }
    return () => clearPageHeaderOverride();
  }, [setPageHeaderOverride, clearPageHeaderOverride, firstName, lastName, fullName, role, isAdmin, teamViewOpen, organization?.name]);

  const isOrgLoading = !!userProfile?.org_id && orgLoading;

  const branding = organization?.branding || {};
  const settings = organization?.settings || {};
  const accentColor = branding.color || branding.accent_color;
  const logoUrl = branding.logo
    ? `${process.env.REACT_APP_API_URL || ''}${branding.logo}`
    : '';

  const organizationName = organization?.name || '';
  const organizationType = settings.type || 'Corporation';
  const teamMemberCount = organization?.member_count;
  const bankName = organization?.bank_name || '';
  const contractPath = branding.contract || '';
  const contractStatus = contractPath ? 'Uploaded' : 'Not uploaded';
  const contractUrl = contractPath
    ? `${process.env.REACT_APP_API_URL || ''}${contractPath}`
    : '';

  const handleSaveOrganization = async (payload) => {
    const orgId = userProfile?.org_id;
    if (!orgId) throw new Error('Organization not loaded');
    const updated = {
      ...(organization || {}),
      name: payload.name,
      phone: payload.phone,
      address: { ...(organization?.address || {}), street: payload.address },
      settings: {
        ...(organization?.settings || {}),
        pocTitle: payload.pocTitle,
        isSole: payload.soleProprietorship,
      },
    };
    await apiClient.updateOrganization(orgId, updated);
    await queryClient.invalidateQueries({ queryKey: ['organization', orgId] });
  };

  const handleSaveContract = async (payload) => {
    const orgId = userProfile?.org_id;
    if (!orgId) throw new Error('Organization not loaded');

    let contractPathNew = organization?.branding?.contract || '';
    const rawFile = payload.file?.raw;
    if (rawFile instanceof File) {
      const renamed = new File(
        [rawFile],
        `${orgId}_${rawFile.name}`,
        { type: rawFile.type }
      );
      const uploaded = await apiClient.uploadFile(renamed, 'contract');
      contractPathNew = uploaded.file_path;
    } else if (!payload.file && !payload.existingUrl) {
      contractPathNew = '';
    }

    await apiClient.updateOrganization(orgId, {
      name: organization?.name,
      type: organization?.type,
      subscription_tier: organization?.subscription_tier,
      branding: {
        ...(organization?.branding || {}),
        contract: contractPathNew,
      },
    });
    await queryClient.invalidateQueries({ queryKey: ['organization', orgId] });
  };

  const handleUpdatePhone = async ({ countryCode, phone }) => {
    const profileId = userProfile?.userprofile_id;
    if (!profileId) throw new Error('User profile not loaded');
    const digits = (phone || '').replace(/\D/g, '');
    const fullPhone = digits ? `${countryCode}${digits}` : '';
    await apiClient.updateUser(profileId, { phone: fullPhone });
    await fetchUserProfile();
  };

  const handleUpdateMember = async (payload, original) => {
    const profileId = original?.userprofile_id;
    if (!profileId) {
      console.error('updateUser: missing userprofile_id on member', original);
      throw new Error('Member profile id is missing');
    }
    const body = {
      first_name: payload.firstName,
      last_name: payload.lastName,
      role: payload.role,
    };
    console.log('updateUser →', profileId, body);
    const result = await apiClient.updateUser(profileId, body);
    console.log('updateUser ←', result);
    await fetchUserProfile();
  };

  const handleInviteMember = async (payload) => {
    const orgId = userProfile?.org_id;
    if (!orgId) throw new Error('Organization not loaded');

    const companyName = organization?.name || '-';
    const inviterName =
      [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ') || 'A team admin';

    const encoded = encodeData({
      firstName: payload.firstName,
      lastName: payload.lastName,
      designation: payload.role,
      role: payload.role,
      email: payload.email,
      org_id: orgId,
      company: companyName,
    });
    const registrationUrl = `${window.location.origin}/register/team?d=${encodeURIComponent(encoded)}`;

    await apiClient.sendMessage({
      From: 'tools@trustjet.ai',
      To: payload.email,
      Cc: organization?.billing_email || '',
      ReplyTo: organization?.billing_email || '',
      Subject: `You've been invited to join ${companyName}`,
      HtmlBody: buildTeamInviteEmailHtml({
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        designation: payload.role,
        companyName,
        inviterName,
        registrationUrl,
        organization,
      }),
    });
  };

  const handleSaveBranding = async (payload) => {
    const orgId = userProfile?.org_id;
    if (!orgId) throw new Error('Organization not loaded');

    let logoPath = organization?.branding?.logo || '';
    if (payload.logoFile instanceof File) {
      const renamed = new File(
        [payload.logoFile],
        `${orgId}_${payload.logoFile.name}`,
        { type: payload.logoFile.type }
      );
      const uploaded = await apiClient.uploadFile(renamed, 'logo');
      logoPath = uploaded.file_path;
    } else if (!payload.logoFile && !payload.logoPreview) {
      logoPath = '';
    }

    await apiClient.updateOrganization(orgId, {
      name: organization?.name,
      type: organization?.type,
      subscription_tier: organization?.subscription_tier,
      branding: {
        ...(organization?.branding || {}),
        color: payload.color,
        custom_colors: payload.customColors,
        logo: logoPath,
      },
    });
    await queryClient.invalidateQueries({ queryKey: ['organization', orgId] });
  };

  const teamMembers = userProfile
    ? [
        {
          id: userProfile.user_id || userProfile.id || user?.id,
          userprofile_id: userProfile.userprofile_id,
          first_name: firstName,
          last_name: lastName,
          email,
          role: role || (isAdmin ? 'admin' : ''),
        },
      ]
    : [];

  return (
    <div className="max-w-3xl pb-8">
      <div className="overflow-hidden">
        <div
          className="flex items-start w-[200%] transition-transform duration-300 ease-out"
          style={{ transform: teamViewOpen ? 'translateX(-50%)' : 'translateX(0)' }}
        >
          <div className="w-1/2 flex-shrink-0">
            <div className="bg-white/70 backdrop-blur-sm rounded-[2.5rem] p-4 md:p-6 space-y-6">
              <SettingsSection label="Account Settings">
                <SettingsRow
                  icon={RiMailLine}
                  title={email || 'Add email address'}
                  subtitle={email ? 'Verified email address' : undefined}
                  hideChevron
                />
                <SettingsRow
                  icon={RiPhoneLine}
                  title={phone || 'Add phone number'}
                  subtitle={phone ? 'Verified phone number' : undefined}
                  onClick={() => setPhoneModalOpen(true)}
                />
                <SettingsRow
                  icon={RiLockLine}
                  title="Change password"
                  subtitle={
                    hasPasswordLogin
                      ? 'Last changed 2 months ago'
                      : `Managed by ${providerLabel || 'your sign-in provider'} — no password to change`
                  }
                  onClick={hasPasswordLogin ? () => setPasswordModalOpen(true) : undefined}
                  disabled={!hasPasswordLogin}
                />
                <SettingsRow
                  icon={RiShieldCheckLine}
                  title="Two-factor authentication"
                  subtitle="One-time passcode"
                  onClick={() => setMfaModalOpen(true)}
                />
              </SettingsSection>

              <SettingsSection label="Organization">
                {isOrgLoading ? (
                  <>
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                  </>
                ) : (
                  <>
                    <SettingsRow
                      icon={RiBuildingLine}
                      title={organizationName || 'Organization'}
                      subtitle={organizationType || 'Corporation'}
                      onClick={() => setOrgDrawerOpen(true)}
                    />
                    <SettingsRow
                      icon={RiPaletteLine}
                      title="Update branding"
                      subtitle={accentColor ? `Logo, Accent ${accentColor}` : 'Logo, Accent color'}
                      onClick={() => setBrandingDrawerOpen(true)}
                    />
                    <SettingsRow
                      icon={RiFileTextLine}
                      title="Standard charter contract"
                      subtitle={contractStatus}
                      onClick={() => setContractModalOpen(true)}
                    />
                    <SettingsRow
                      icon={RiTeamLine}
                      title="Manage team members"
                      subtitle={
                        typeof teamMemberCount === 'number'
                          ? `${teamMemberCount} member${teamMemberCount === 1 ? '' : 's'}`
                          : 'Invite and manage your team'
                      }
                      onClick={() => setTeamViewOpen(true)}
                    />
                  </>
                )}
              </SettingsSection>

              <SettingsSection label="Banking">
                {isOrgLoading ? (
                  <SkeletonRow />
                ) : (
                  <SettingsRow
                    icon={RiBankLine}
                    title="Update banking details"
                    subtitle={bankName || 'Add your bank account'}
                    onClick={() =>
                      toast.info('This section is under development', { position: 'bottom-right' })
                    }
                  />
                )}
              </SettingsSection>
            </div>
          </div>
          <div className="w-1/2 flex-shrink-0">
            <TeamMembersPanel
              members={teamMembers}
              currentUserId={userProfile?.user_id || userProfile?.id || user?.id}
              onAddMember={() => {
                setEditingMember(null);
                setAddMemberOpen(true);
              }}
              onEditMember={(member) => {
                setEditingMember({
                  id: member.id,
                  userprofile_id: member.userprofile_id,
                  firstName: member.first_name || '',
                  lastName: member.last_name || '',
                  email: member.email || '',
                  role: member.role || '',
                });
                setAddMemberOpen(true);
              }}
            />
          </div>
        </div>
      </div>

      <UpdatePhoneModal
        open={phoneModalOpen}
        onClose={() => setPhoneModalOpen(false)}
        initialPhone={userProfile?.phone || ''}
        onSubmit={handleUpdatePhone}
      />

      <ChangePasswordModal
        open={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
      />

      <EditOrganizationDrawer
        open={orgDrawerOpen}
        onClose={() => setOrgDrawerOpen(false)}
        initialValues={{
          name: organizationName,
          address: organization?.address?.street || '',
          phone: organization?.phone || userProfile?.phone || '',
          pocTitle: settings.pocTitle || '',
          soleProprietorship: !!settings.isSole,
        }}
        onSubmit={handleSaveOrganization}
      />

      <UpdateBrandingDrawer
        open={brandingDrawerOpen}
        onClose={() => setBrandingDrawerOpen(false)}
        initialValues={{
          color: accentColor || '',
          logoPreview: logoUrl,
          customColors: branding.custom_colors || [],
        }}
        onSubmit={handleSaveBranding}
      />

      <StandardContractModal
        open={contractModalOpen}
        onClose={() => setContractModalOpen(false)}
        initialUrl={contractUrl}
        onSubmit={handleSaveContract}
      />

      <TwoFactorAuthModal
        open={mfaModalOpen}
        onClose={() => setMfaModalOpen(false)}
      />

      <AddMemberDrawer
        open={addMemberOpen}
        onClose={() => {
          setAddMemberOpen(false);
          setEditingMember(null);
        }}
        initialValues={editingMember}
        onSubmit={(payload, original) =>
          original ? handleUpdateMember(payload, original) : handleInviteMember(payload)
        }
      />
    </div>
  );
};

export default AccountSettings;
